import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
  ChevronLeftIcon,
  MapPinIcon,
  ShareIcon,
  HeartIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  XMarkIcon,
  ArrowsPointingOutIcon
} from '@heroicons/react/24/outline';
import { LinkIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Star, Check } from 'lucide-react';
import toast from 'react-hot-toast';

import { exploreAPI, savedAPI, createQueryKey, chatAPI } from '../services/api';
import UnifiedMap from '../components/UnifiedMap';
import EditProjectModal from '../components/EditProjectModal';
import BookmarkModal from '../components/BookmarkModal';
import ReviewsSection from '../components/reviews/ReviewsSection';
import ReviewModal from '../components/reviews/ReviewModal';
import { useAuth } from '../contexts/AuthContext';
import { canEditProject } from '../utils/permissions';
import tagsData from '../data/tags.json';
import { normalizeProjectGeolocation, getProjectTrailPoints, projectHasTrail } from '../utils/projectLocation';

// Error boundary for map component to handle Leaflet navigation errors
class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a Leaflet-related error
    if (error.message && (
      error.message.includes('_leaflet_pos') ||
      error.message.includes('_getMapPanePos') ||
      error.message.includes('leaflet')
    )) {
      return { hasError: true };
    }
    // For other errors, let them bubble up
    throw error;
  }

  componentDidCatch(error, errorInfo) {
    // Log Leaflet errors but don't crash the app
    console.warn('Map error caught during navigation:', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // Reset error state when projectId changes (new page)
    if (prevProps.projectId !== this.props.projectId && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      // Return a simple placeholder instead of the map
      return (
        <div className="w-full h-full bg-gradient-to-br from-green-100 to-blue-100 dark:from-green-800 dark:to-blue-800 flex items-center justify-center rounded-lg">
          <div className="text-center text-gray-600 dark:text-gray-400">
            <MapPinIcon className="w-12 h-12 mx-auto mb-2 text-green-600 dark:text-green-400" />
            <p className="text-sm">Map loading...</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Helper function to check if geolocation is a trail
const isTrail = (geolocation) => {
  return projectHasTrail({ geolocation });
};

// Helper function to calculate proper map initialization for trails
const getMapConfig = (geolocation) => {
  const centerPoint = normalizeProjectGeolocation({ geolocation });
  const trailPoints = getProjectTrailPoints({ geolocation });

  if (trailPoints.length > 1) {
    const lats = trailPoints.map((p) => p.lat);
    const lngs = trailPoints.map((p) => p.lng);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Calculate center
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    // Calculate appropriate zoom level based on bounds
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    let zoom;
    if (maxDiff > 0.1) zoom = 10;
    else if (maxDiff > 0.05) zoom = 11;
    else if (maxDiff > 0.02) zoom = 12;
    else if (maxDiff > 0.01) zoom = 13;
    else if (maxDiff > 0.005) zoom = 14;
    else zoom = 15;

    return {
      center: [centerLat, centerLng],
      zoom,
      bounds: [[minLat, minLng], [maxLat, maxLng]],
    };
  }

  if (centerPoint) {
    return {
      center: [centerPoint.lat, centerPoint.lng],
      zoom: 14,
    };
  }

  return { center: [47.6062, -122.3321], zoom: 10 };
};

const ExploreDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  
  // Force stop all map animations
  const stopMapAnimations = useCallback(() => {
    try {
      // Clear all global timeouts first
      if (window.mapCleanupTimeouts) {
        window.mapCleanupTimeouts.forEach(id => clearTimeout(id));
        window.mapCleanupTimeouts.clear();
      }
      
      const mapInstance = mapInstanceRef.current;
      if (mapInstance) {
        // Stop all animations immediately
        mapInstance.stop();
        mapInstance.stopLocate?.();
        
        // Force clear any pending transitions
        if (mapInstance._mapPane) {
          mapInstance._mapPane.style.transform = '';
        }
        
        // Clear any zoom transition callbacks
        if (mapInstance._animatingZoom) {
          mapInstance._animatingZoom = false;
        }
        
        // Clear transition timers
        if (mapInstance._zoomAnimated) {
          mapInstance.fire('zoomend');
        }
      }
    } catch (e) {
      // Ignore cleanup errors
      console.warn('Map cleanup error:', e);
    }
  }, []);

  // Context-aware navigation - remember where we came from
  const navigateBack = () => {
    stopMapAnimations();
    
    const previousPath = location.state?.previousPath || location.state?.referrer;
    const targetUrl = previousPath || '/starters'; // Default to starters if no previous path
    
    setTimeout(() => {
      navigate(targetUrl, {
        state: {
          previousPath: '/starters/' + projectId,
          transitionDirection: 'back',
        },
      });
    }, 50); // Small delay to ensure map cleanup
  };
  
  // Get context-aware back button text
  const getBackButtonText = () => {
    const previousPath = location.state?.previousPath || location.state?.referrer;
    if (previousPath && previousPath.includes('/saved/')) {
      return 'Back to List';
    }
    return 'Back to Starters';
  };
  
  // No user geolocation on this page; focus on project geolocation/trail
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Bookmark modal state
  const [bookmarkModal, setBookmarkModal] = useState({ isOpen: false, projectId: null, projectName: null });
  
  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  
  // Touch/swipe state for mobile image slider
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const imageSliderRef = useRef(null);
  
  // Map ref for cleanup during navigation
  const mapInstanceRef = useRef(null);
  
  // Scroll detection for mobile header background
  const [isScrolledPastImage, setIsScrolledPastImage] = useState(false);
  const mobileHeroRef = useRef(null);
  
  // Animation configuration following the app's pattern
  const carouselTransition = {
    type: 'tween',
    ease: [0.4, 0, 0.2, 1], // Material Design easing
    duration: 0.3, // 300ms for smooth but quick
  };

  // (intentionally no geolocation request on this page)

  // Touch/swipe handlers for mobile image slider
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    if (isAnimating) return; // Prevent new gestures during animation
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    if (isAnimating || !touchStart) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || isAnimating) {
      // Reset touch state even if we don't process the gesture
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    const distance = touchEnd ? Math.abs(touchStart - touchEnd) : 0;
    const isSwipe = distance > minSwipeDistance;
    const isLeftSwipe = touchEnd && (touchStart - touchEnd) > minSwipeDistance;
    const isRightSwipe = touchEnd && (touchStart - touchEnd) < -minSwipeDistance;
    
    // Clear touch state immediately to prevent multiple triggers
    setTouchStart(null);
    setTouchEnd(null);
    
    // If it's a tap (not a swipe) on mobile, navigate to images page
    if (!isSwipe && isMobileDevice() && project?.images && project.images.length > 0) {
      stopMapAnimations();
      setTimeout(() => {
        navigate(`/starters/${projectId}/images`);
      }, 50);
      return;
    }
    
    // Handle swipes
    if (isLeftSwipe && project?.images && selectedImageIndex < project.images.length - 1) {
      setIsAnimating(true);
      setSelectedImageIndex(prev => prev + 1);
      // Reset animation flag after transition
      setTimeout(() => setIsAnimating(false), carouselTransition.duration * 1000);
    } else if (isRightSwipe && selectedImageIndex > 0) {
      setIsAnimating(true);
      setSelectedImageIndex(prev => prev - 1);
      // Reset animation flag after transition
      setTimeout(() => setIsAnimating(false), carouselTransition.duration * 1000);
    }
  };
  
  // Fetch project details
  const { 
    data: projectResponse, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['explore-project', projectId],
    queryFn: () => exploreAPI.getById(projectId),
    enabled: !!projectId,
    keepPreviousData: true, // Prevent loading state during navigation
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const project = projectResponse?.data?.data?.project;

  // Navigation cleanup to prevent map errors
  useEffect(() => {
    return () => {
      // Stop map animations immediately on cleanup
      stopMapAnimations();
    };
  }, [stopMapAnimations]);

  // No geolocation effect here

  // Scroll detection for mobile header background - using IntersectionObserver
  useEffect(() => {
    const observerCallback = (entries) => {
      const [entry] = entries;
      setIsScrolledPastImage(!entry.isIntersecting);
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.3,
      rootMargin: '0px'
    });

    const currentRef = mobileHeroRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, []); // Run once on mount

  // Get project's saved status
  const { data: savedStatusResponse } = useQuery({
    queryKey: createQueryKey.projectSavedStatus(projectId),
    queryFn: () => savedAPI.getProjectStatus(projectId),
    enabled: !!user && !!projectId,
    keepPreviousData: true, // Prevent loading state during navigation
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const savedStatus = savedStatusResponse?.data?.data || { isSaved: false, savedInLists: [] };

  // No longer need clone mutation - projects are stored locally first


  // Handle project start - clone, start session, and navigate to chat
  const startProjectMutation = useMutation({
    mutationKey: ['clone-project', project?.id],
    mutationFn: async () => {
      console.log('[ExploreDetailPage] Cloning project:', {
        projectId: project.id,
        name: project.name,
        archetype: project.settings?.archetype,
        settings: project.settings
      });
      // Clone the project via API
      const cloneResponse = await exploreAPI.clone(project.id);
      const newProjectId = cloneResponse.data.data.projectId;
      
      if (!newProjectId) {
        throw new Error('Project cloned but missing ID');
      }
      
      // Start a new session for the cloned project
      const sessionResponse = await chatAPI.startNewSession(newProjectId);
      const chatId = sessionResponse?.data?.data?.chatId || sessionResponse?.data?.chatId;
      
      if (!chatId) {
        throw new Error('Session started but missing chat ID');
      }
      
      return { newProjectId, chatId };
    },
    onSuccess: async ({ newProjectId, chatId }) => {
      console.log('[ExploreDetailPage] Clone and session start successful:', {
        newProjectId,
        chatId
      });
      
      // Invalidate and refetch all project-related queries to ensure fresh data
      // This includes user-projects (used by ProjectsPage) and projects (general)
      // Use refetchQueries to force immediate refetch, not just mark as stale
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
      queryClient.refetchQueries({ queryKey: ['user-projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries(createQueryKey.project(newProjectId));
      queryClient.invalidateQueries({ queryKey: ['project-chats', newProjectId] });
      
      // Stop map animations before navigation
      stopMapAnimations();
      
      // Wait a bit longer to ensure backend has fully committed
      // Navigate directly to the chat interface
      setTimeout(() => {
        console.log('[ExploreDetailPage] Navigating to chat:', `/projects/${newProjectId}/chats/${chatId}`);
        navigate(`/projects/${newProjectId}/chats/${chatId}`, { replace: true });
      }, 200);
    },
    onError: (error) => {
      console.error('[ExploreDetailPage] Clone or session start failed:', error);
      toast.error('Failed to start project');
    }
  });

  const handleCloneProject = () => {
    if (!project) return;
    if (!user) {
      toast.error('Please login to start a project');
      return;
    }
    
    // Prevent multiple simultaneous calls
    if (startProjectMutation.isPending || startProjectMutation.isLoading) {
      console.log('[ExploreDetailPage] Clone already in progress, ignoring duplicate call');
      return;
    }
    
    startProjectMutation.mutate();
  };

  // Handle opening edit modal
  const handleEditProject = () => {
    if (!canEditProject(user, project)) {
      toast.error('You do not have permission to edit this project');
      return;
    }
    setIsEditModalOpen(true);
  };


  // Handle bookmark button click
  const handleBookmarkClick = () => {
    if (!user) {
      toast.error('Please login to save projects');
      return;
    }
    
    if (!project) return;
    
    setBookmarkModal({ 
      isOpen: true, 
      projectId: project.id, 
      projectName: project.name 
    });
  };

  const closeBookmarkModal = () => {
    setBookmarkModal({ isOpen: false, projectId: null, projectName: null });
  };

  // Share helpers
  const isMobileDevice = () => {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent || navigator.vendor || '';
    const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) || coarsePointer;
  };

  const handleShareClick = async () => {
    const url = window.location.href;
    const title = project?.name || document.title;
    const text = 'Check this out';
    const isMobile = isMobileDevice();
    const hasNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
    if (hasNativeShare && isMobile) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        // Do not show modal on mobile; fallback to copying link
        await handleCopyLink();
        return;
      }
    }
    if (isMobile) {
      await handleCopyLink();
      return;
    }
    setIsShareModalOpen(true);
  };

  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = url;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      toast.success('Link copied to clipboard');
    } catch (e) {
      // toast.error('Could not copy link');
    }
  };

  const handleEmailShare = () => {
    const subject = `Check out this project: ${project?.name || ''}`.trim();
    const body = `${project?.name ? project.name + ' — ' : ''}${window.location.href}`;
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };


  // Format distance for display
  // const formatDistance = (distance) => {
  //   if (!distance) return '';
  //   return distance < 1 ? `${(distance * 1000).toFixed(0)}m` : `${distance.toFixed(1)}km`;
  // };

  // Format duration for display
  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  };

  // Get formatted location from server (pre-computed)
  // const formattedLocation = project?.formattedLocation;


  // Show loading only for initial load, not during navigation
  if (isLoading && !project) {
    return (
      <div className="h-full bg-white dark:bg-gray-900">
        {/* Header skeleton */}
        <div className="lg:dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
              <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-48 animate-pulse"></div>
            </div>
          </div>
        </div>
        
        {/* Content skeleton */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="w-full h-64 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
              <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-3/4 animate-pulse"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 animate-pulse"></div>
            </div>
            <div className="w-full h-64 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="h-full bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Project not found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">The project you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={navigateBack}
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
          >
{getBackButtonText()}
          </button>
        </div>
      </div>
    );
  }

  // Shared key stats section (used for both desktop and mobile)
  const hasKeyStats = project.owner || (project.rating && project.rating.average > 0) || isTrail(project.geolocation) || project.trailLength || project.elevationGain || project.estimatedDuration || project.difficulty;
  
  const keyStatsContent = hasKeyStats ? (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4`}>
      {/* Author - First stat box */}
      {project.owner && (
        <button 
          onClick={() => {
            stopMapAnimations();
            setTimeout(() => navigate(`/users/${project.owner.username}`), 50);
          }}
          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-left hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all group"
        >
          <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Starter, by</div>
          <div className="flex items-center gap-2">
            {project.owner.avatar ? (
              <img src={require('../config/runtime').getImageUrl(project.owner.avatar)} className="w-7 h-7 rounded-full object-cover" alt="" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center text-xs text-white font-bold">
                {(project.owner.firstName?.[0] || project.owner.username?.[0])?.toUpperCase()}
              </div>
            )}
            <span className="text-lg lg:text-2xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
              {project.owner.username}
            </span>
          </div>
        </button>
      )}
      {/* Rating - Second stat box */}
      {project.rating && project.rating.average > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Rating</div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            <span className="text-lg lg:text-2xl font-bold text-gray-900 dark:text-white">{project.rating.average.toFixed(1)}</span>
            {project.rating.count > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">({project.rating.count})</span>
            )}
          </div>
        </div>
      )}
      {isTrail(project.geolocation) && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Waypoints</div>
          <div className="text-lg lg:text-2xl font-bold text-gray-900 dark:text-white">{getProjectTrailPoints(project).length}</div>
        </div>
      )}
      {project.trailLength && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Length</div>
          <div className="text-lg lg:text-2xl font-bold text-gray-900 dark:text-white">{project.trailLength.value} {project.trailLength.unit}</div>
        </div>
      )}
      {project.elevationGain && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Elevation gain</div>
          <div className="text-lg lg:text-2xl font-bold text-gray-900 dark:text-white">{project.elevationGain}m</div>
        </div>
      )}
      {project.estimatedDuration && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Est. time</div>
          <div className="text-lg lg:text-2xl font-bold text-gray-900 dark:text-white">{formatDuration(project.estimatedDuration)}</div>
        </div>
      )}
      {project.difficulty && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Difficulty</div>
          <div className="text-lg lg:text-2xl font-bold text-gray-900 dark:text-white">{project.difficulty.charAt(0).toUpperCase() + project.difficulty.slice(1)}</div>
        </div>
      )}
    </div>
  ) : null;


  return (
    <div className="h-full bg-white dark:bg-gray-900 min-h-[100dvh]">
      {/* Mobile Fixed Top Controls - TWO BARS: one for over-image, one for scrolled */}
      
      {/* Bar 1: Always visible when NOT scrolled past image (transparent, dark buttons) */}
      <div 
        className={`md:hidden fixed top-0 left-0 right-0 z-[2000] bg-transparent ${
          isScrolledPastImage ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="h-14 flex items-center justify-between px-4 max-w-7xl mx-auto w-full">
          <button
            onClick={navigateBack}
            className="p-2 rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/50"
          >
            <ChevronLeftIcon className="w-6 h-6 ml-[-2px]" />
          </button>
          <div className="flex items-center gap-2">
            <button onClick={handleShareClick} className="p-2 rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/50">
              <ShareIcon className="w-5 h-5" />
            </button>
            <button 
              onClick={handleBookmarkClick}
              className={`p-2 rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/50 ${savedStatus.isSaved ? 'text-red-400' : ''}`}
            >
              {savedStatus.isSaved ? <HeartSolidIcon className="w-5 h-5" /> : <HeartIcon className="w-5 h-5" />}
            </button>
            {canEditProject(user, project) && (
              <button onClick={handleEditProject} className="p-2 rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/50">
                <PencilIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Bar 2: Only visible when scrolled past image (white bg, light buttons) */}
      {isScrolledPastImage && (
        <div 
          className="md:hidden fixed top-0 left-0 right-0 z-[2000] bg-white/90 dark:bg-gray-900/90 shadow-sm backdrop-blur-sm animate-fade-in"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className="h-14 flex items-center justify-between px-4 max-w-7xl mx-auto w-full">
            <button
              onClick={navigateBack}
              className="p-2 rounded-full text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ChevronLeftIcon className="w-6 h-6 ml-[-2px]" />
            </button>
            <div className="flex items-center gap-2">
              <button onClick={handleShareClick} className="p-2 rounded-full text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">
                <ShareIcon className="w-5 h-5" />
              </button>
              <button 
                onClick={handleBookmarkClick}
                className={`p-2 rounded-full text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 ${savedStatus.isSaved ? 'text-yellow-500' : ''}`}
              >
                {savedStatus.isSaved ? <HeartSolidIcon className="w-5 h-5" /> : <HeartIcon className="w-5 h-5" />}
              </button>
              {canEditProject(user, project) && (
                <button onClick={handleEditProject} className="p-2 rounded-full text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">
                  <PencilIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== MOBILE: Single Image with Carousel ===== */}
      <div className="md:hidden" ref={mobileHeroRef}>
        <div 
          className="relative aspect-[4/3] bg-gray-100 dark:bg-gray-800"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          ref={imageSliderRef}
        >
          {project.images && project.images.length > 0 ? (
            <>
              <motion.div
                className="flex h-full"
                animate={{ 
                  transform: `translateX(-${selectedImageIndex * (100 / project.images.length)}%)`
                }}
                transition={carouselTransition}
                style={{
                  width: `${project.images.length * 100}%`,
                  willChange: 'transform',
                }}
              >
                {project.images.map((image, index) => (
                  <div
                    key={index}
                    className="relative flex-shrink-0 h-full"
                    style={{ width: `${100 / project.images.length}%` }}
                  >
                    <img
                      src={require('../config/runtime').getImageUrl(image)}
                      alt={`${project.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading={index === 0 ? 'eager' : 'lazy'}
                    />
                  </div>
                ))}
              </motion.div>
              {/* Image Counter Badge */}
              {project.images.length > 1 && (
                <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-gray-900/80 backdrop-blur-sm text-white text-sm font-medium rounded-lg">
                  {selectedImageIndex + 1} / {project.images.length}
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-green-100 to-blue-100 dark:from-green-900 dark:to-blue-900 flex items-center justify-center">
              <MapPinIcon className="w-16 h-16 text-green-600/50 dark:text-green-400/50" />
            </div>
          )}
        </div>
        
        {/* Mobile Title Section */}
        <div className="px-6 pt-5">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2 leading-tight">
            {project.name}
          </h1>
          
          {/* Subtitle info */}
          {/* <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
            {project.owner && (
              <button 
                onClick={() => {
                  stopMapAnimations();
                  setTimeout(() => navigate(`/users/${project.owner.username}`), 50);
                }}
                className="flex items-center gap-1.5"
              >
                {project.owner.avatar ? (
                  <img src={require('../config/runtime').getImageUrl(project.owner.avatar)} className="w-5 h-5 rounded-full object-cover" alt="" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center text-[8px] text-white font-bold">
                    {(project.owner.firstName?.[0] || project.owner.username?.[0])?.toUpperCase()}
                  </div>
                )}
                <span className="underline decoration-gray-300 dark:decoration-gray-600 underline-offset-2">
                  {project.owner.username}
                </span>
              </button>
            )}
            {project.rating && project.rating.average > 0 && (
              <>
                {project.owner && <span>·</span>}
                <div className="flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                  <span>{project.rating.average.toFixed(1)}</span>
                </div>
              </>
            )}
          </div> */}
        </div>
      </div>

      {/* ===== DESKTOP: Title Header + Image Grid (Airbnb Style) ===== */}
      <div className="hidden md:block">
        {/* Desktop Header with Title */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-6">
          {/* Back Link */}
          <button
            onClick={navigateBack}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4 transition-colors"
          >
            <ChevronLeftIcon className="h-4 w-4 mr-1" />
            {getBackButtonText()}
          </button>
          
          {/* Title Row */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-white leading-tight">
                {project.name}
              </h1>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-1">
              <button 
                onClick={handleShareClick} 
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ShareIcon className="w-4 h-4" />
                <span className="hidden lg:inline">Share</span>
              </button>
              <button 
                onClick={handleBookmarkClick}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${
                  savedStatus.isSaved ? 'text-yellow-500' : 'text-gray-700 dark:text-gray-200'
                }`}
              >
                {savedStatus.isSaved ? (
                  <HeartSolidIcon className="w-4 h-4" />
                ) : (
                  <HeartIcon className="w-4 h-4" />
                )}
                <span className="hidden lg:inline">Save</span>
              </button>
              {canEditProject(user, project) && (
                <button
                  onClick={handleEditProject}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Image Grid - Adapts based on number of images */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="relative rounded-xl overflow-hidden cursor-pointer group"
            onClick={() => {
              stopMapAnimations();
              setTimeout(() => navigate(`/starters/${projectId}/images`), 50);
            }}
          >
            {project.images && project.images.length >= 5 ? (
              /* 5+ images: Full grid layout */
              <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[400px]">
                <div className="col-span-2 row-span-2 relative overflow-hidden rounded-l-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[0])} alt={project.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden">
                  <img src={require('../config/runtime').getImageUrl(project.images[1])} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-tr-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[2])} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden">
                  <img src={require('../config/runtime').getImageUrl(project.images[3])} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-br-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[4])} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  <button
                    onClick={(e) => { e.stopPropagation(); stopMapAnimations(); setTimeout(() => navigate(`/starters/${projectId}/images`), 50); }}
                    className="absolute bottom-3 right-3 px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                      <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" /><circle cx="14" cy="2" r="1.5" />
                      <circle cx="2" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="14" cy="8" r="1.5" />
                      <circle cx="2" cy="14" r="1.5" /><circle cx="8" cy="14" r="1.5" /><circle cx="14" cy="14" r="1.5" />
                    </svg>
                    Show all photos
                  </button>
                </div>
              </div>
            ) : project.images && project.images.length === 4 ? (
              /* 4 images */
              <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[400px]">
                <div className="col-span-2 row-span-2 relative overflow-hidden rounded-l-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[0])} alt={project.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden">
                  <img src={require('../config/runtime').getImageUrl(project.images[1])} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-tr-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[2])} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="col-span-2 relative overflow-hidden rounded-br-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[3])} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  <button
                    onClick={(e) => { e.stopPropagation(); stopMapAnimations(); setTimeout(() => navigate(`/starters/${projectId}/images`), 50); }}
                    className="absolute bottom-3 right-3 px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                      <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" /><circle cx="14" cy="2" r="1.5" />
                      <circle cx="2" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="14" cy="8" r="1.5" />
                      <circle cx="2" cy="14" r="1.5" /><circle cx="8" cy="14" r="1.5" /><circle cx="14" cy="14" r="1.5" />
                    </svg>
                    Show all photos
                  </button>
                </div>
              </div>
            ) : project.images && project.images.length === 3 ? (
              /* 3 images */
              <div className="grid grid-cols-3 grid-rows-2 gap-2 h-[400px]">
                <div className="col-span-2 row-span-2 relative overflow-hidden rounded-l-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[0])} alt={project.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-tr-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[1])} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-br-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[2])} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  <button
                    onClick={(e) => { e.stopPropagation(); stopMapAnimations(); setTimeout(() => navigate(`/starters/${projectId}/images`), 50); }}
                    className="absolute bottom-3 right-3 px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                      <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" /><circle cx="14" cy="2" r="1.5" />
                      <circle cx="2" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="14" cy="8" r="1.5" />
                      <circle cx="2" cy="14" r="1.5" /><circle cx="8" cy="14" r="1.5" /><circle cx="14" cy="14" r="1.5" />
                    </svg>
                    Show all photos
                  </button>
                </div>
              </div>
            ) : project.images && project.images.length === 2 ? (
              /* 2 images */
              <div className="grid grid-cols-2 gap-2 h-[400px]">
                <div className="relative overflow-hidden rounded-l-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[0])} alt={project.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <div className="relative overflow-hidden rounded-r-xl">
                  <img src={require('../config/runtime').getImageUrl(project.images[1])} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  <button
                    onClick={(e) => { e.stopPropagation(); stopMapAnimations(); setTimeout(() => navigate(`/starters/${projectId}/images`), 50); }}
                    className="absolute bottom-3 right-3 px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                      <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" /><circle cx="14" cy="2" r="1.5" />
                      <circle cx="2" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="14" cy="8" r="1.5" />
                      <circle cx="2" cy="14" r="1.5" /><circle cx="8" cy="14" r="1.5" /><circle cx="14" cy="14" r="1.5" />
                    </svg>
                    Show all photos
                  </button>
                </div>
              </div>
            ) : project.images && project.images.length === 1 ? (
              /* 1 image */
              <div className="relative overflow-hidden rounded-xl w-full h-[400px]">
                <img src={require('../config/runtime').getImageUrl(project.images[0])} alt={project.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </div>
            ) : (
              /* No images */
              <div className="h-[300px] bg-gradient-to-br from-green-100 to-blue-100 dark:from-green-900 dark:to-blue-900 rounded-xl flex items-center justify-center">
                <MapPinIcon className="w-24 h-24 text-green-600/30 dark:text-green-400/30" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-4 lg:pt-6 pb-32 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8">
           {/* Left Column */}
           <div className="space-y-8">
              {/* Key Stats Grid */}
              {hasKeyStats && (
                 <div>
                    {keyStatsContent}
                 </div>
              )}

              {/* Description */}
              {project.description && (
                 <div className="prose dark:prose-invert max-w-none">
                    <div className={`relative ${!showFullDescription && project.description.length > 600 ? 'max-h-40 overflow-hidden' : ''}`}>
                       <p className="text-gray-700 dark:text-gray-300 text-base lg:text-lg leading-relaxed whitespace-pre-line">
                         {project.description}
                       </p>
                       {!showFullDescription && project.description.length > 600 && (
                         <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white dark:from-gray-900 to-transparent" />
                       )}
                    </div>
                    {project.description.length > 600 && (
                      <button 
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="mt-2 text-blue-600 dark:text-blue-400 font-medium hover:underline"
                      >
                        {showFullDescription ? 'Show less' : 'Read more'}
                      </button>
                    )}
                 </div>
              )}

              {/* Tags */}
               {project.tags && project.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tagId, index) => {
                      const tagInfo = tagsData.tags.find(t => t.id === tagId);
                      return (
                        <span 
                          key={index} 
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                        >
                          {tagInfo ? (
                            <>
                              <span>{tagInfo.icon}</span>
                              <span>{tagInfo.label}</span>
                            </>
                          ) : (
                            <span>#{tagId}</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
               )}

              {/* AI Prompt */}
              {project.prompt && project.prompt.trim() && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    AI Prompt
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-600/50 rounded-lg p-4">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed italic text-sm">
                      "{project.prompt}"
                    </p>
                  </div>
                </div>
              )}
           </div>

           {/* Right Column */}
           <div className="space-y-6">

              {/* Objectives Card */}
              {project.objectives && project.objectives.length > 0 && (
                <div className="">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-2">
                    Objectives
                  </h3>
                  <div className="border border-gray-100 dark:border-gray-700/50 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50 rounded-2xl shadow bg-white dark:bg-gray-800">
                    {project.objectives
                      .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
                      .slice(0, 5)
                      .map((obj, index) => {
                        const isCompleted = obj.isCompleted === true;
                        return (
                          <div 
                            key={index}
                            className={`flex items-center gap-3 px-3 py-3 ${
                              isCompleted 
                                ? 'bg-green-50/50 dark:bg-green-900/5' 
                                : 'bg-white dark:bg-gray-800/30'
                            }`}
                          >
                            {/* Completion indicator */}
                            <div 
                              className={`flex-shrink-0 w-[18px] h-[18px] rounded border-2 flex items-center justify-center ${
                                isCompleted 
                                  ? 'bg-green-500 border-green-500 text-white' 
                                  : 'border-gray-300 dark:border-gray-500'
                              }`}
                            >
                              {isCompleted && <Check className="w-3 h-3" strokeWidth={3} />}
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm leading-tight ${
                                isCompleted
                                  ? 'line-through text-gray-400 dark:text-gray-500'
                                  : 'text-gray-900 dark:text-white'
                              }`}>
                                {obj.objectiveName || 'Goal'}
                              </span>
                              {obj.objectiveDescription && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                                  {obj.objectiveDescription}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {project.objectives.length > 5 && (
                    <div className="pt-3 text-center">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        + {project.objectives.length - 5} more objectives
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Environments Card */}
              {/* {project.environments && project.environments.length > 0 && (
                 <div className="rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 p-5">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Environments</h3>
                    <div className="flex flex-wrap gap-2">
                       {project.environments.map((envRel, index) => (
                          <span key={index} className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                             {envRel.environment?.name}
                          </span>
                       ))}
                    </div>
                 </div>
              )} */}
           </div>
        </div>
              
        {/* Map Card - Now in sidebar on desktop, stacked on mobile */}
        {project.geolocation && (
          <div className="mt-8">
             <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  Where you'll be
                </h3>
                {/* <button 
                  onClick={() => setIsMapModalOpen(true)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  Expand map
                </button> */}
             </div>
             
             {/* Location String */}
             <div className="mb-6 text-gray-600 dark:text-gray-300 font-medium">
               {project.formattedLocation || (
                 <div className="flex gap-2">
                   <span>{project.location?.city || 'Unknown City'},</span>
                   <span>{project.location?.region || 'Unknown Region'},</span>
                   <span>{project.location?.country || 'Unknown Country'}</span>
                 </div>
               )}
             </div>

             <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 relative group">
                <div className="h-80 relative">
                  <MapErrorBoundary projectId={projectId}>
                    <UnifiedMap 
                      key={`map-sidebar-${projectId}`}
                      mode="view"
                      projects={[project]}
                      layoutContext="contained"
                      center={getMapConfig(project.geolocation).center}
                      zoom={getMapConfig(project.geolocation).zoom}
                      bounds={getMapConfig(project.geolocation).bounds}
                      showUserLocation={true}
                      showProjectNumbers={false}
                      // No interaction on small map
                      interactive={false}
                    />
                  </MapErrorBoundary>
                  
                  {/* Click overlay to open modal */}
                  <button
                    onClick={() => setIsMapModalOpen(true)}
                    className="absolute inset-0 z-10 bg-transparent cursor-pointer"
                    aria-label="Open map"
                  />

                  {/* Expand Button Overlay */}
                  <div className="absolute top-4 right-4 z-[999]">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMapModalOpen(true);
                      }}
                      className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 p-2 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      aria-label="Expand map"
                    >
                      <ArrowsPointingOutIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
             </div>
          </div>
        )}

        {/* Reviews Section (Moved to bottom full width) - Use template aggregation for explore projects */}
        <div className="pt-8">
          <ReviewsSection 
            projectId={projectId} 
            isTemplate={true}
            onOpenReview={(review) => { setEditingReview(review || null); setIsReviewOpen(true); }} 
          />
        </div>
      </div>

      {/* Fixed Bottom Button (Start Project) */}
      <div className="fixed z-[1000] bottom-0 left-0 right-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-t lg:border lg:max-w-[350px] mx-auto lg:rounded-full lg:shadow-xl lg:mb-6 border-gray-200 dark:border-gray-700 py-4 px-6 safe-area-pb transition-transform duration-300">
        <button
          onClick={handleCloneProject}
          disabled={startProjectMutation.isPending || startProjectMutation.isLoading}
          className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3.5 rounded-full font-bold transition-all shadow-lg shadow-green-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
        >
          <PlayIcon className="w-5 h-5 fill-current" />
          <span>{startProjectMutation.isPending || startProjectMutation.isLoading ? 'Starting...' : 'Start Project'}</span>
        </button>
      </div>

  {/* Fullscreen Map Modal (Desktop) */}
  {isMapModalOpen && (
    <div className="fixed inset-0 z-[2000] bg-black/70">
      <div className="absolute inset-0">
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 h-14 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center px-4 gap-2">
          <button
            onClick={() => setIsMapModalOpen(false)}
            className="w-9 h-9 rounded-full border border-gray-200 dark:border-gray-700 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800"
            aria-label="Close map"
          >
            <XMarkIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{project?.name}</div>
        </div>

        {/* Map area */}
        <div className="pt-14 h-full">
          <MapErrorBoundary projectId={projectId}>
            <UnifiedMap 
              key={`map-modal-${projectId}`}
              mode="view"
              projects={[project]}
              layoutContext="contained"
              showFollowButton={false}
              userLocation={null}
              center={getMapConfig(project.geolocation).center}
              zoom={getMapConfig(project.geolocation).zoom}
              bounds={getMapConfig(project.geolocation).bounds}
              showUserLocation={true}
              showProjectNumbers={false}
            />
          </MapErrorBoundary>
        </div>
      </div>
    </div>
  )}

      {/* Edit Project Modal */}
      <EditProjectModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        project={project}
        onSuccess={() => {
          // The modal handles query invalidation and will trigger a refetch
          // No additional action needed here
        }}
      />


      {/* Bookmark Modal */}
      <BookmarkModal
        isOpen={bookmarkModal.isOpen}
        onClose={closeBookmarkModal}
        projectId={bookmarkModal.projectId}
        projectName={bookmarkModal.projectName}
      />


      {/* Share Modal */}
      {isShareModalOpen && !isMobileDevice() && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center"
          aria-modal="true"
          role="dialog"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsShareModalOpen(false)}
          />
          <div className="relative z-[2001] w-full max-w-md mx-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-3xl shadow-2xl overflow-hidden">
            <div className="divide-y divide-gray-200 dark:divide-gray-800">
              <div className="flex items-center justify-between px-5 py-4">
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Share this project</h3>
                <button
                  onClick={() => setIsShareModalOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
              {/* {project?.name && (
                <div className="px-5 text-sm text-gray-600 dark:text-gray-400">{project.name}</div>
              )} */}
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                    <LinkIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  </span>
                  <span className="text-gray-900 dark:text-white font-medium">Copy link</span>
                </div>
              </button>
              <button
                onClick={handleEmailShare}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                    <EnvelopeIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  </span>
                  <span className="text-gray-900 dark:text-white font-medium">Share via email</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      <ReviewModal projectId={projectId} isOpen={isReviewOpen} onClose={() => { setIsReviewOpen(false); setEditingReview(null); }} existingReview={editingReview} />
    </div>
  );
};

export default ExploreDetailPage;
